/**
 * ThemeController — applies the UI store's theme + palette to <html>
 * (design-revamp, handoff-2026-06).
 *
 * Renders nothing. In effects it writes two `data-*` attributes that
 * `@garden/config-tailwind/theme.css` keys off:
 *   - `document.documentElement.dataset.theme`   → "light" | "dark"
 *   - `document.documentElement.dataset.palette` → the active brand palette
 *
 * The store's `theme` can be "system"; this component resolves that to the
 * concrete "light"/"dark" the stylesheet understands by reading the OS
 * preference via `matchMedia('(prefers-color-scheme: dark)')`. While the
 * user keeps "system", a `change` listener re-applies the resolved theme
 * when the OS flips light↔dark; switching to an explicit "light"/"dark"
 * tears the listener down (the effect re-runs on `theme` change).
 *
 * jsdom guard: `window.matchMedia` is undefined under Vitest's jsdom env,
 * so every access is gated on `typeof window.matchMedia === "function"`.
 * When it is absent, "system" falls back to "light" (the GardEn default
 * theme) and no listener is attached.
 *
 * This is mounted once near the provider-tree root (main.tsx). It is the
 * single authoritative writer of `data-theme`/`data-palette`; nothing else
 * should set those attributes.
 */
import { useEffect } from "react";
import type { ReactElement } from "react";

import { useUiStore, type Theme } from "@/lib/ui-store";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/** True when the jsdom/SSR `matchMedia` shim is unavailable. */
function hasMatchMedia(): boolean {
  return (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
  );
}

/**
 * Resolve a (possibly "system") theme preference to the concrete
 * "light"/"dark" the stylesheet keys off. "system" reads the OS
 * preference; when `matchMedia` is unavailable it defaults to "light".
 */
function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") {
    return theme;
  }
  if (hasMatchMedia()) {
    return window.matchMedia(COLOR_SCHEME_QUERY).matches ? "dark" : "light";
  }
  return "light";
}

export function ThemeController(): ReactElement | null {
  const theme = useUiStore((state) => state.theme);
  const palette = useUiStore((state) => state.palette);

  // Theme axis: resolve "system" and keep it in sync with the OS while the
  // user stays on "system".
  useEffect(() => {
    const apply = (): void => {
      document.documentElement.dataset.theme = resolveTheme(theme);
    };
    apply();

    // Only "system" needs to react to OS changes; explicit light/dark is fixed.
    if (theme !== "system" || !hasMatchMedia()) {
      return;
    }

    const mql = window.matchMedia(COLOR_SCHEME_QUERY);
    const onChange = (): void => {
      apply();
    };
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, [theme]);

  // Palette axis: independent of theme.
  useEffect(() => {
    document.documentElement.dataset.palette = palette;
  }, [palette]);

  return null;
}

export default ThemeController;
